export const BAIDU_AIGC_MATERIAL_IDS_OPTION = '__baiduAigcMaterialIds';
export const BAIDU_ASSET_REF_PREFIX = 'asset://';

export type BaiduAigcAssetType = 'Image' | 'Video' | 'Audio';

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getBaiduAssetIdFromReference(url: string): string | undefined {
  if (!url.toLowerCase().startsWith(BAIDU_ASSET_REF_PREFIX)) return undefined;
  const assetId = url.slice(BAIDU_ASSET_REF_PREFIX.length).trim();
  return assetId || undefined;
}

export function buildBaiduMaterialLookupKey(
  url: string,
  assetType: BaiduAigcAssetType,
): string {
  // 与 ensure / buildAigcMaterialInputs 统一 trim，避免 content URL 带空白时映射 miss。
  return `${assetType}::${url.trim()}`;
}

export function toBaiduAssetReference(assetId: string): string {
  return `${BAIDU_ASSET_REF_PREFIX}${assetId}`;
}

/**
 * 将百度 Seedance 任务载荷里的原始 URL 替换为已提审的 asset:// 引用。
 * 映射由 Inngest 审核步骤通过私有 provider option 注入；未命中则保留原 URL（fallback）。
 */
export function applyBaiduMaterialReferences(
  input: JsonRecord,
  providerOptions?: Record<string, any>,
): JsonRecord {
  const materialIds = providerOptions?.[BAIDU_AIGC_MATERIAL_IDS_OPTION];
  if (!isRecord(materialIds)) return input;

  const resolveUrl = (url: unknown, assetType: BaiduAigcAssetType): unknown => {
    if (typeof url !== 'string' || !url) return url;
    const existingAssetId = getBaiduAssetIdFromReference(url);
    if (existingAssetId) return toBaiduAssetReference(existingAssetId);
    const assetId = materialIds[buildBaiduMaterialLookupKey(url, assetType)];
    return typeof assetId === 'string' && assetId
      ? toBaiduAssetReference(assetId)
      : url;
  };

  if (!Array.isArray(input.content)) return input;

  let changed = false;
  const content = input.content.map((item) => {
    if (!isRecord(item)) return item;

    if (item.type === 'image_url' && isRecord(item.image_url)) {
      const url = resolveUrl(item.image_url.url, 'Image');
      if (url === item.image_url.url) return item;
      changed = true;
      return {
        ...item,
        image_url: { ...item.image_url, url },
      };
    }

    if (item.type === 'video_url' && isRecord(item.video_url)) {
      const url = resolveUrl(item.video_url.url, 'Video');
      if (url === item.video_url.url) return item;
      changed = true;
      return {
        ...item,
        video_url: { ...item.video_url, url },
      };
    }

    if (item.type === 'audio_url' && isRecord(item.audio_url)) {
      const url = resolveUrl(item.audio_url.url, 'Audio');
      if (url === item.audio_url.url) return item;
      changed = true;
      return {
        ...item,
        audio_url: { ...item.audio_url, url },
      };
    }

    return item;
  });

  return changed ? { ...input, content } : input;
}
